import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from './store';
import { setCredentials, updateUser } from './store/authSlice';
import { LoginPage } from './pages/LoginPage';
import { StravaCallback } from './pages/auth/StravaCallback';
import { TrackLibrary } from './pages/TrackLibrary';
import { UploadPage } from './pages/UploadPage';
import { PreviewPage } from './pages/PreviewPage';
import { RenderStatusPage } from './pages/RenderStatusPage';
import { PaymentSuccessPage } from './pages/payment/PaymentSuccessPage';
import { PaymentCancelPage } from './pages/payment/PaymentCancelPage';
import { connectSocket, disconnectSocket, getSocket } from './utils/socket';
import { apiGet } from './utils/api';
import { CookieConsent } from './components/CookieConsent';
import { ImpersonationBanner } from './components/common/ImpersonationBanner';
import { UIProvider, useUI } from './context/UIContext';

// Dynamic Lazy Loading for Admin Panel bundle & assets (loaded ONLY when visiting /admin)
const AdminPage = React.lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })),
);

// ProtectedRoute Wrapper Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const ui = useUI();

  useEffect(() => {
    if (isAuthenticated && token) {
      connectSocket();
    } else {
      disconnectSocket();
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    const socket = getSocket();
    if (socket && isAuthenticated) {
      const handleSubscriptionStatus = (data: { plan: string; status: string }) => {
        if (data.status === 'expired') {
          ui.alert(
            'Your premium subscription plan has expired. Your account has been switched back to the Free plan.',
            'Subscription Expired'
          );
        }
        apiGet('/api/auth/profile')
          .then((profile) => {
            if (profile?.user) {
              dispatch(updateUser(profile.user));
            }
          })
          .catch((err) => console.error('Failed to reload profile after subscription update:', err));
      };

      socket.on('subscription_status', handleSubscriptionStatus);
      return () => {
        socket.off('subscription_status', handleSubscriptionStatus);
      };
    }
  }, [isAuthenticated, dispatch, ui]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      dispatch(setCredentials({ token: tokenParam }));
    }
  }, [dispatch]);

  return (
    <BrowserRouter>
      <ImpersonationBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/strava/callback" element={<StravaCallback />} />

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0b0f19', color: '#9ca3af' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px' }}>
                      <div className="spinner" style={{ width: '24px', height: '24px' }} />
                      <span>Loading Admin Module...</span>
                    </div>
                  </div>
                }
              >
                <AdminPage />
              </React.Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/library"
          element={
            <ProtectedRoute>
              <TrackLibrary />
            </ProtectedRoute>
          }
        />

        <Route
          path="/upload"
          element={
            <ProtectedRoute>
              <UploadPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/preview/:id"
          element={
            <ProtectedRoute>
              <PreviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/status/:jobId"
          element={
            <ProtectedRoute>
              <RenderStatusPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payment/success"
          element={
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payment/cancel"
          element={
            <ProtectedRoute>
              <PaymentCancelPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/payment/settings"
          element={
            <ProtectedRoute>
              <Navigate to="/library?settings=billing" replace />
            </ProtectedRoute>
          }
        />

        {/* Redirect root in app to library */}
        <Route path="/" element={<Navigate to="/library" replace />} />

        {/* Fallback to Library */}
        <Route path="*" element={<Navigate to="/library" replace />} />
      </Routes>
      <CookieConsent />
    </BrowserRouter>
  );
};

const App: React.FC = () => {
  return (
    <UIProvider>
      <AppContent />
    </UIProvider>
  );
};

export default App;
